/**
 * Life4Earth V2 — Authentication Context
 *
 * Manages JWT authentication state for the application.
 * Stores access token in memory (not localStorage) for security.
 * Refresh token is stored in memory and used for silent token renewal.
 *
 * Usage:
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 *
 *   const { user, isAuthenticated, login, logout } = useAuth();
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL, setAuthenticatedUserGuid } from '../api/client';
import { setAuthBridge } from '../api/authBridge';
import { clearAll as clearQueryCache } from '../api/queryCache';
import { flushClientStateForIdentitySwitch } from './identityFlush';

// ── Types ───────────────────────────────────────────────────

export interface AuthUser {
  userGuid: string;
  email: string;
  displayName: string;
  profileImageUrl?: string;
  isSystemAdmin: boolean;
  /** DEV-720 — 18+ (server-computed from DOB). Gates open-feed commenting. */
  isAdult?: boolean;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // seconds
}

/** Profile fields collected during registration */
export interface RegistrationProfile {
  firstName: string;
  lastName: string;
  dateOfBirth: string; // yyyy-MM-dd
  homeCommunityGuid?: string;
  /** DEV-1413 (slice 2) — declared operating community. Optional; the
      register endpoint defaults it to homeCommunityGuid (§8.11 ruling 1). */
  operatingCommunityGuid?: string;
  eulaAccepted: boolean;
  tosAccepted: boolean;
  /** Onboarding §7.5 — community-standards acknowledgment. Collected on the
      invitation-accept form; optional on bare self-serve signup. */
  communityStandardsAccepted?: boolean;
  /** Onboarding Steps 1–3 — the raw invitation token when this registration
      is an invitation accept (DEV-1307). Drives day-zero: consume invite,
      lineage edge, org membership activation. Null = self-serve signup. */
  inviteToken?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean; // true during initial token refresh check
  accessToken: string | null;
  login: (provider: 'google' | 'apple', idToken: string) => Promise<{ requires2FA: boolean }>;
  /** Email/password login (DEV-143) */
  emailLogin: (email: string, password: string) => Promise<{ requires2FA: boolean }>;
  /** Email/password registration with profile fields */
  emailRegister: (email: string, password: string, profile: RegistrationProfile) => Promise<void>;
  /** Request password reset email (DEV-143) */
  forgotPassword: (email: string) => Promise<string>;
  /** Reset password using token (DEV-143) */
  resetPassword: (token: string, newPassword: string) => Promise<string>;
  logout: () => Promise<void>;
  /** Whether FakeAuth mode is active (no real auth required) */
  isFakeAuth: boolean;
  /** Admin impersonation (DEV-555) */
  impersonate: (userGuid: string) => Promise<void>;
  endImpersonate: () => Promise<void>;
  /** True when the current session is an impersonated identity */
  isImpersonating: boolean;
  /** The admin's display name (shown in the impersonation banner) */
  impersonatorDisplayName: string | null;
  /** True when login succeeded but 2FA verification is required */
  requires2FA: boolean;
  /** Partial user info available during 2FA challenge */
  pending2FAUser: AuthUser | null;
  /** Verify a TOTP code to complete 2FA login */
  verifyTotp: (code: string, trustDevice?: boolean) => Promise<void>;
  /** Verify a recovery code to complete 2FA login */
  verifyRecoveryCode: (code: string) => Promise<void>;
  /** Cancel the 2FA flow and return to login */
  cancel2FA: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Module-level token storage ──────────────────────────────
// Access tokens live in memory only (short-lived, security best practice).
// Refresh tokens are persisted to localStorage so silentRefresh can
// reissue a fresh access token after a page reload.

const REFRESH_TOKEN_KEY = 'l4e_refresh_token';

let _accessToken: string | null = null;
let _refreshToken: string | null = localStorage.getItem(REFRESH_TOKEN_KEY);

function persistRefreshToken(token: string | null) {
  _refreshToken = token;
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

// Impersonation state: stash the admin's tokens so we can restore them later
let _adminAccessToken: string | null = null;
let _adminRefreshToken: string | null = null;
let _adminUserGuid: string | null = null;
let _adminDisplayName: string | null = null;

/**
 * Get the current access token (used by client.ts for Bearer injection).
 * Returns null if not authenticated.
 */
export function getAccessToken(): string | null {
  return _accessToken;
}

/**
 * Module-level mirror of the user's sysadmin flag, kept in sync with React
 * state by an effect in AuthProvider. Allows non-React modules (e.g. the
 * API health monitor) to gate admin-only checks without subscribing to
 * the AuthContext. Defaults to false when not signed in. DEV-896 cleanup.
 */
let _isSystemAdmin: boolean = false;

export function getIsSystemAdmin(): boolean {
  return _isSystemAdmin;
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Called by apiFetch on 401 responses to transparently recover from
 * expired or missing access tokens. Returns the new access token on
 * success, or null if refresh is not possible.
 */
export async function tryRefreshToken(): Promise<string | null> {
  const rt = _refreshToken || localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!rt) return null;

  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });

    if (!res.ok) {
      // Refresh token is invalid/expired — clear it
      persistRefreshToken(null);
      _accessToken = null;
      return null;
    }

    const data = await res.json();

    // DEV-1469 / POLICY_ACCOUNT_SECURITY_LADDER rule 6 — this is now the RARE
    // LEGACY PATH, not the everyday one.
    //
    // A refresh token minted through a completed TOTP verification carries
    // two_factor_verified and now rotates without a fresh challenge (admins
    // included), so a verified session survives a reload. Only tokens that never
    // passed TOTP land here: rows predating migration 274, and mints outside the
    // verify paths (impersonation). Fail closed — they get bounced to a full
    // login, which re-challenges properly and mints a flagged token.
    //
    // The behavior is unchanged and correct: this caller cannot render the 2FA
    // UI, so a challenge on a silent refresh is an auth failure from here.
    if (data.requires2FA) {
      persistRefreshToken(null);
      _accessToken = null;
      return null;
    }

    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);
    return _accessToken;
  } catch {
    return null;
  }
}

// ── Web auth bridge registration (DEV-1418) ────────────────
//
// client.ts no longer imports this module directly (that coupling made the
// shared API client un-importable from React Native). Instead it calls
// through src/api/authBridge.ts, and we register the web implementation
// here at module scope — the same evaluation timing as the old direct
// import, so behavior is unchanged: Bearer injection from the in-memory
// access token, transparent refresh from the localStorage refresh token,
// and the two window CustomEvents on terminal auth failure / suspension.
setAuthBridge({
  getAccessToken,
  tryRefreshToken,
  onAuthRequired: () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('l4e:auth-required'));
    }
  },
  onAccountSuspended: (detail: unknown) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('l4e:account-suspended', { detail }));
    }
  },
});

// ── Provider ────────────────────────────────────────────────

interface AuthProviderProps {
  children: React.ReactNode;
  /** When true, skip auth entirely (dev mode with FakeAuthMiddleware) */
  fakeAuth?: boolean;
}

export function AuthProvider({ children, fakeAuth = false }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(!fakeAuth); // Don't load in fake auth mode
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror the user's sysadmin flag to module scope for non-React consumers
  // (e.g., apiHealthService gates admin-only health checks). Always reflects
  // the current React state; cleared when the user logs out.
  useEffect(() => {
    _isSystemAdmin = user?.isSystemAdmin ?? false;
  }, [user]);

  // ── 2FA challenge state ─────────────────────────────────────
  const [requires2FA, setRequires2FA] = useState(false);
  const [pending2FAUser, setPending2FAUser] = useState<AuthUser | null>(null);
  const [partialToken, setPartialToken] = useState<string | null>(null);

  // Schedule a silent token refresh before the access token expires
  const scheduleRefresh = useCallback((expiresInSeconds: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    // Refresh 60 seconds before expiry (or at half-life if short-lived)
    const refreshMs = Math.max((expiresInSeconds - 60) * 1000, expiresInSeconds * 500);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        await silentRefresh();
      } catch {
        // Refresh failed — user will be redirected to login on next API call
        _accessToken = null;
        persistRefreshToken(null);
        setAuthenticatedUserGuid(null);
        setUser(null);
      }
    }, refreshMs);
  }, []);

  // Perform a silent token refresh
  const silentRefresh = useCallback(async () => {
    if (!_refreshToken) return;

    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: _refreshToken }),
    });

    // Rate-limited — wait and retry once
    if (res.status === 429) {
      const retryAfter = parseInt(res.headers.get('Retry-After') || '30', 10);
      await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
      return silentRefresh(); // single retry after cooldown
    }

    if (!res.ok) {
      throw new Error('Refresh failed');
    }

    const data = await res.json();

    // If refresh response requires 2FA, enter challenge state instead of full auth.
    //
    // DEV-946: do NOT wipe the refresh token here. The server returned a
    // partialToken — meaning the refresh token authenticated successfully and
    // only the second factor is missing. Wiping it created a multi-tab kick-
    // out loop: tab 2 demands TOTP → wipes localStorage → tab 1 (and any
    // future tab open) finds no token → user is sent to fresh login. Keep
    // the refresh token; verifyTotp / verifyRecoveryCode rotate it normally
    // on TOTP completion. The token is only cleared by explicit logout or
    // when a refresh attempt actually fails (the !res.ok path above).
    if (data.requires2FA) {
      _accessToken = null;
      setPartialToken(data.partialToken);
      setPending2FAUser(data.user);
      setRequires2FA(true);
      return;
    }

    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);

    setAuthenticatedUserGuid(data.user.userGuid);
    setUser(data.user);
    scheduleRefresh(data.expiresIn);
  }, [scheduleRefresh]);

  // Login with OAuth provider
  const login = useCallback(async (provider: 'google' | 'apple', idToken: string): Promise<{ requires2FA: boolean }> => {
    const endpoint = provider === 'google' ? '/api/auth/google' : '/api/auth/apple';

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Login failed' }));
      if (res.status === 429) {
        const retrySeconds = err.retryAfterSeconds ?? parseInt(res.headers.get('Retry-After') || '60', 10);
        throw new Error(`Too many login attempts. Please wait ${retrySeconds} seconds and try again.`);
      }
      throw new Error(err.message || 'Login failed');
    }

    const data = await res.json();
    // 2FA required — enter challenge state
    if (data.requires2FA) {
      setPartialToken(data.partialToken);
      setPending2FAUser(data.user);
      setRequires2FA(true);
      return { requires2FA: true };
    }

    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);

    setAuthenticatedUserGuid(data.user.userGuid);
    setUser(data.user);
    scheduleRefresh(data.expiresIn);
    return { requires2FA: false };
  }, [scheduleRefresh]);

  // ── Email/Password login (DEV-143) ──────────────────────────
  const emailLogin = useCallback(async (email: string, password: string): Promise<{ requires2FA: boolean }> => {
    const res = await fetch(`${API_BASE_URL}/api/auth/email-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Login failed' }));
      if (res.status === 429) {
        throw new Error(err.message || 'Too many login attempts. Please try again later.');
      }
      throw new Error(err.message || 'Invalid email or password.');
    }

    const data = await res.json();

    // 2FA required — enter challenge state
    if (data.requires2FA) {
      setPartialToken(data.partialToken);
      setPending2FAUser(data.user);
      setRequires2FA(true);
      return { requires2FA: true }; // Don't set full auth — wait for TOTP verification
    }

    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);

    setAuthenticatedUserGuid(data.user.userGuid);
    setUser(data.user);
    scheduleRefresh(data.expiresIn);
    return { requires2FA: false };
  }, [scheduleRefresh]);

  // ── Email/Password registration (DEV-143) ──────────────────
  const emailRegister = useCallback(async (email: string, password: string, profile: RegistrationProfile) => {
    const res = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        firstName: profile.firstName,
        lastName: profile.lastName,
        dateOfBirth: profile.dateOfBirth,
        homeCommunityGuid: profile.homeCommunityGuid || null,
        operatingCommunityGuid: profile.operatingCommunityGuid || null,
        eulaAccepted: profile.eulaAccepted,
        tosAccepted: profile.tosAccepted,
        communityStandardsAccepted: profile.communityStandardsAccepted || false,
        inviteToken: profile.inviteToken || null,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Registration failed' }));
      if (res.status === 429) {
        throw new Error(err.message || 'Too many registration attempts. Please try again later.');
      }
      throw new Error(err.message || 'Registration failed.');
    }

    const data = await res.json();
    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);

    setAuthenticatedUserGuid(data.user.userGuid);
    setUser(data.user);
    scheduleRefresh(data.expiresIn);
  }, [scheduleRefresh]);

  // ── TOTP 2FA verification ──────────────────────────────────
  //
  // Audit F6 (2026-05-22): the authenticated user state is now populated
  // from data.user returned by the verify endpoint, NOT from the stored
  // pending2FAUser. The pending user no longer carries displayName or
  // profileImageUrl (the backend strips them in the 2FA challenge
  // response). The verify endpoints return the full profile because by
  // then the user is fully authenticated. Fallback to pendingUser is
  // retained for backward compatibility while the API rolls out — drop
  // it after one release cycle.
  const verifyTotp = useCallback(async (code: string, trustDevice = false) => {
    if (!partialToken) throw new Error('No pending 2FA session');

    const res = await fetch(`${API_BASE_URL}/api/totp/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${partialToken}`,
      },
      body: JSON.stringify({ code, trustDevice }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Verification failed' }));
      throw new Error(err.error || 'Invalid verification code.');
    }

    const data = await res.json();
    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);

    // Clear 2FA state
    setRequires2FA(false);
    setPartialToken(null);
    const pendingUser = pending2FAUser;
    setPending2FAUser(null);

    const verifiedUser: AuthUser | null = data.user ?? pendingUser ?? null;
    if (verifiedUser) {
      setAuthenticatedUserGuid(verifiedUser.userGuid);
      setUser(verifiedUser);
    }
    scheduleRefresh(data.expiresIn);
  }, [partialToken, pending2FAUser, scheduleRefresh]);

  const verifyRecoveryCode = useCallback(async (code: string) => {
    if (!partialToken) throw new Error('No pending 2FA session');

    const res = await fetch(`${API_BASE_URL}/api/totp/verify-recovery`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${partialToken}`,
      },
      body: JSON.stringify({ code }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Verification failed' }));
      throw new Error(err.error || 'Invalid recovery code.');
    }

    const data = await res.json();
    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);

    setRequires2FA(false);
    setPartialToken(null);
    const pendingUser = pending2FAUser;
    setPending2FAUser(null);

    const verifiedUser: AuthUser | null = data.user ?? pendingUser ?? null;
    if (verifiedUser) {
      setAuthenticatedUserGuid(verifiedUser.userGuid);
      setUser(verifiedUser);
    }
    scheduleRefresh(data.expiresIn);
  }, [partialToken, pending2FAUser, scheduleRefresh]);

  const cancel2FA = useCallback(() => {
    setRequires2FA(false);
    setPartialToken(null);
    setPending2FAUser(null);
  }, []);

  // ── Forgot password (DEV-143) ──────────────────────────────
  const forgotPassword = useCallback(async (email: string): Promise<string> => {
    const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(err.message || 'Request failed.');
    }

    const data = await res.json();
    return data.message;
  }, []);

  // ── Reset password (DEV-143) ───────────────────────────────
  const resetPassword = useCallback(async (token: string, newPassword: string): Promise<string> => {
    const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Reset failed' }));
      throw new Error(err.message || 'Password reset failed.');
    }

    const data = await res.json();
    return data.message;
  }, []);

  // ── Admin Impersonation (DEV-555) ──────────────────────────
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatorDisplayName, setImpersonatorDisplayName] = useState<string | null>(null);

  const impersonate = useCallback(async (targetUserGuid: string) => {
    const res = await fetch(`${API_BASE_URL}/api/admin/users/${targetUserGuid}/impersonate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${_accessToken}`,
      },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Impersonation failed' }));
      throw new Error(err.message || 'Impersonation failed');
    }

    const data = await res.json();

    // Stash the admin's current session before switching
    _adminAccessToken = _accessToken;
    _adminRefreshToken = _refreshToken;
    _adminUserGuid = data.adminUserGuid;
    _adminDisplayName = user?.displayName || null;

    // Switch to the target user's session
    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);

    setAuthenticatedUserGuid(data.user.userGuid);
    setUser(data.user);
    setIsImpersonating(true);
    setImpersonatorDisplayName(_adminDisplayName);
    scheduleRefresh(data.expiresIn);
  }, [scheduleRefresh, user]);

  const endImpersonate = useCallback(async () => {
    if (!_adminUserGuid) return;

    const res = await fetch(`${API_BASE_URL}/api/admin/users/end-impersonate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminUserGuid: _adminUserGuid }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Failed to end impersonation' }));
      throw new Error(err.message || 'Failed to end impersonation');
    }

    const data = await res.json();

    // Restore admin session
    _accessToken = data.accessToken;
    persistRefreshToken(data.refreshToken);
    _adminAccessToken = null;
    _adminRefreshToken = null;
    _adminUserGuid = null;
    _adminDisplayName = null;

    setAuthenticatedUserGuid(data.user.userGuid);
    setUser(data.user);
    setIsImpersonating(false);
    setImpersonatorDisplayName(null);
    scheduleRefresh(data.expiresIn);
  }, [scheduleRefresh]);

  // Logout
  const logout = useCallback(async () => {
    if (_refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: _refreshToken }),
        });
      } catch {
        // Ignore logout API errors — clear local state regardless
      }
    }

    _accessToken = null;
    persistRefreshToken(null);
    setAuthenticatedUserGuid(null);
    setUser(null);

    // DEV-1443: clear the departing user's per-user client caches on logout so
    // they can't linger for the NEXT login on a shared browser (the App
    // identity-sync flush only covers a direct A→B switch; a logout resets the
    // prev-identity ref to null, so B's later login would look like a fresh
    // bootstrap and skip that flush — this closes that path).
    flushClientStateForIdentitySwitch();

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // On mount, try to silently refresh using the persisted refresh token.
  // The refresh token is stored in localStorage so it survives page reloads.
  // On success, a fresh access token is issued and stored in memory.
  useEffect(() => {
    if (fakeAuth) return;

    if (_refreshToken) {
      silentRefresh()
        .catch(() => { /* No valid refresh token */ })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [fakeAuth, silentRefresh]);

  // Cleanup refresh timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // ── Global terminal-401 listener (audit F4) ───────────────────
  //
  // The API client (apiFetch in src/api/client.ts) dispatches this event
  // when a protected request returns 401 AND tryRefreshToken() returns
  // null. That combination means the session is unrecoverable — the
  // refresh token is gone, revoked, or expired. We wipe local session
  // state so SessionGate sees isAuthenticated=false and re-renders into
  // the /login redirect path.
  //
  // The wipe scope was chosen per Skip's policy answer earlier in the
  // audit (refresh token + queryCache, NOT UI prefs):
  //   - clear in-memory access token
  //   - clear refresh token from localStorage
  //   - clear authenticated user GUID in the API client
  //   - clear React user state and 2FA pending state
  //   - clear the queryCache (drops any cached server data so a fresh
  //     login doesn't briefly render stale info from the prior session)
  //   - cancel the scheduled silent-refresh timer
  //
  // localStorage entries NOT cleared: theme prefs, language, last-active
  // community, gantt state, panel collapse states. Those are user
  // experience preferences, not server data, and survive a re-login.
  useEffect(() => {
    if (fakeAuth) return;

    function handleAuthRequired() {
      _accessToken = null;
      persistRefreshToken(null);
      setAuthenticatedUserGuid(null);
      setUser(null);
      setRequires2FA(false);
      setPartialToken(null);
      setPending2FAUser(null);
      clearQueryCache();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }

    window.addEventListener('l4e:auth-required', handleAuthRequired);
    return () => {
      window.removeEventListener('l4e:auth-required', handleAuthRequired);
    };
  }, [fakeAuth]);

  const value: AuthContextValue = {
    user: fakeAuth ? {
      userGuid: 'FB60AFB9-C52A-9B28-8EB3-C0B600FD248D',
      email: 'dev@life4earth.com',
      displayName: 'Dev User',
      isSystemAdmin: true,
    } : user,
    isAuthenticated: fakeAuth || user !== null,
    isLoading,
    accessToken: _accessToken,
    login,
    emailLogin,
    emailRegister,
    forgotPassword,
    resetPassword,
    logout,
    isFakeAuth: fakeAuth,
    impersonate,
    endImpersonate,
    isImpersonating,
    impersonatorDisplayName,
    requires2FA,
    pending2FAUser: pending2FAUser,
    verifyTotp,
    verifyRecoveryCode,
    cancel2FA,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
